import { Loader2, MessageSquare } from "lucide-react";
import { useState } from "react";
import { useAuth } from "../contexts/AuthContext";
import { Button } from "./ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "./ui/dialog";
import { Textarea } from "./ui/textarea";
import { useToast } from "./ui/use-toast";

interface IssueReportDialogProps {
  isOpen: boolean;
  onClose: () => void;
  postId: string;
  connectionId: string;
  platform: string;
  errorCode: string;
  errorMessage: string;
}

export function IssueReportDialog({
  isOpen,
  onClose,
  postId,
  connectionId,
  platform,
  errorCode,
  errorMessage,
}: IssueReportDialogProps) {
  const [additionalInfo, setAdditionalInfo] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { currentUser } = useAuth();
  const { toast } = useToast();

  const handleSubmit = async () => {
    if (!currentUser) {
      toast({
        title: "Error",
        description: "You must be logged in to report an issue.",
        variant: "destructive",
      });
      return;
    }

    setIsSubmitting(true);
    try {
      const idToken = await currentUser.getIdToken();
      const response = await fetch(
        `${process.env.REACT_APP_API_BASE}/issue-reports`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${idToken}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            postId,
            connectionId,
            platform,
            errorCode,
            errorMessage,
            additionalInfo: additionalInfo.trim() || undefined,
          }),
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || "Failed to submit report");
      }

      toast({
        title: "Issue Reported",
        description: "Thank you for reporting this issue. We'll look into it.",
      });

      setAdditionalInfo("");
      onClose();
    } catch (error) {
      console.error("Error submitting issue report:", error);
      toast({
        title: "Error",
        description: "Failed to submit issue report. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <MessageSquare className="h-5 w-5" />
            Report Issue
          </DialogTitle>
          <DialogDescription>
            Help us understand what went wrong so we can fix it.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          <div className="rounded-lg bg-gray-50 p-3 text-sm">
            <div className="text-gray-500 mb-1">Platform</div>
            <div className="font-medium capitalize">{platform}</div>
          </div>

          <div className="rounded-lg bg-gray-50 p-3 text-sm">
            <div className="text-gray-500 mb-1">Error</div>
            <div className="font-medium text-red-600">{errorMessage}</div>
          </div>

          <div>
            <label className="text-sm font-medium text-gray-700 mb-2 block">
              Additional Details (Optional)
            </label>
            <Textarea
              placeholder="Describe what you were trying to do when this error occurred..."
              value={additionalInfo}
              onChange={(e) => setAdditionalInfo(e.target.value)}
              rows={4}
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={isSubmitting}>
            {isSubmitting ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Submitting...
              </>
            ) : (
              "Submit Report"
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
